import React, { createContext, useRef, useState } from 'react';
import { CHAT_TAB_VIEWS, Response, Source, DetailSource, ChatHistory, ResponseWithSource, STATUS } from '../types';
import { INITIAL_CHATBOT_MESSAGE } from '../chatbot/config';

interface ChatbotContextType {
  resetApp: boolean;
  setResetApp: (flag: boolean) => void;
  selectedResponseId: string | null;
  setSelectedResponseId: (id: string | null) => void;
  responseDetailsSources: DetailSource;
  setResponseDetailsSources: (sources: DetailSource) => void;
  sourceHistory: { [key: string]: DetailSource };
  setSourceHistory: (sourceHistory: { [key: string]: DetailSource }) => void;
  searchBatchesHistory: { [key: string]: any };
  setSearchBatchesHistory: (searchBatchesHistory: { [key: string]: any }) => void;
  storyResponses: ResponseWithSource[];
  setStoryResponses: (storyResponses: ResponseWithSource[]) => void;
  chatHistory: string;
  setChatHistory: (history: string) => void;
  activeChatTab: CHAT_TAB_VIEWS;
  setActiveChatTab: (tab: CHAT_TAB_VIEWS) => void;
  showCitation: boolean | null;
  setShowCitation: (flag: boolean) => void;
  genArcPromptAnalyticsUId: any;
  setGenArcPromptAnalyticsUId: (genArcPromptAnalyticsUId: any) => void;
  genArcHistoryPrompts: any;
  setGenArcHistoryPrompts: (genArcHistoryPrompts: any) => void;
  chatPromptResponseHistory: ChatHistory[];
  setChatPromptResponseHistory: (history: ChatHistory[]) => void;
  selectedProject: string[] | null;
  setSelectedProject: (project: string[] | null) => void;
  selectedTranscript: string | null;
  setSelectedTranscript: (transcript: string | null) => void;
  isLikeActive: boolean;
  setIsLikeActive: (flag: boolean) => void;
  isDislikeActive: boolean;
  setIsDislikeActive: (flag: boolean) => void;
  chatWindowConversations: any;
  setChatWindowConversations: (messages: any) => void;
  feedbackSubmit: boolean;
  setFeedbackSubmit: (flag: boolean) => void;
  showChatHistoryPanel: boolean;
  setShowChatHistoryPanel: (flag: boolean) => void;
  showRightPanel: boolean;
  setShowRightPanel: (flag: boolean) => void;
  isChatHistoryActive: boolean;
  setIsChatHistoryActive: (flag: boolean) => void;
  prompt: string;
  setPrompt: (prompt: string) => void;
  fetchChatHistoryFlag: boolean;
  setFetchChatHistoryFlag: (flag: boolean) => void;
  activeChatUID: string;
  setActiveChatUID: (uid: any) => void;
  highScore: number;
  setHighScore: (highScore: number) => void;
  currentGenArcPromptAnalyticsUId: string;
  setCurrentGenArcPromptAnalyticsUId: (Uid: string) => void;
  isPromptCancelling: boolean;
  setIsPromptCancelling: (flag: boolean) => void;
  instanceId: string;
  setInstanceId: (id: string) => void;
  promptCancellationStatus: STATUS;
  setPromptCancellationStatus: (status: STATUS) => void;
  closeRef: any;
  showCancelButton: any;
  setShowCancelButton: (flag: any) => void;
}

const defaultChatbotContext = {
  resetApp: false,
  setResetApp: (flag: boolean) => undefined,
  selectedResponseId: null,
  setSelectedResponseId: (id: string | null) => undefined,
  responseDetailsSources: { showCitation: false, sourceList: [] },
  setResponseDetailsSources: (sources: DetailSource) => undefined,
  sourceHistory: {},
  setSourceHistory: (sourceHistory: { [key: string]: DetailSource }) => undefined,
  searchBatchesHistory: {},
  setSearchBatchesHistory: (searchBatchesHistory: { [key: string]: any }) => undefined,
  storyResponses: [],
  setStoryResponses: (storyResponses: ResponseWithSource[]) => undefined,
  chatHistory: `AI: ${INITIAL_CHATBOT_MESSAGE}\n`,
  setChatHistory: (history: string) => undefined,
  activeChatTab: CHAT_TAB_VIEWS.RESPONSE_DETAILS,
  setActiveChatTab: (tab: CHAT_TAB_VIEWS) => undefined,
  showCitation: true,
  setShowCitation: (flag: boolean) => undefined,
  genArcPromptAnalyticsUId: {},
  setGenArcPromptAnalyticsUId: (genArcPromptAnalyticsUId: any) => undefined,
  genArcHistoryPrompts: {},
  setGenArcHistoryPrompts: (genArcHistoryPrompts: any) => undefined,
  chatPromptResponseHistory: [],
  setChatPromptResponseHistory: (history: ChatHistory[]) => undefined,
  selectedProject: null,
  setSelectedProject: (project: string[] | null) => undefined,
  selectedTranscript: null,
  setSelectedTranscript: (transcript: string | null) => undefined,
  isLikeActive: false,
  setIsLikeActive: (flag: boolean) => undefined,
  isDislikeActive: false,
  setIsDislikeActive: (flag: boolean) => undefined,
  chatWindowConversations: [],
  setChatWindowConversations: (messages: any) => undefined,
  feedbackSubmit: false,
  setFeedbackSubmit: (flag: boolean) => undefined,
  showChatHistoryPanel: false,
  setShowChatHistoryPanel: (flag: boolean) => undefined,
  showRightPanel: true,
  setShowRightPanel: (flag: boolean) => undefined,
  isChatHistoryActive: false,
  setIsChatHistoryActive: (flag: boolean) => undefined,
  prompt: '',
  setPrompt: (prompt: string) => undefined,
  fetchChatHistoryFlag: true,
  setFetchChatHistoryFlag: (flag: boolean) => undefined,
  activeChatUID: '',
  setActiveChatUID: (uid: any) => undefined,
  highScore: 0,
  setHighScore: (highScore: number) => undefined,
  currentGenArcPromptAnalyticsUId: '',
  setCurrentGenArcPromptAnalyticsUId: (Uid: string) => undefined,
  isPromptCancelling: false,
  setIsPromptCancelling: (flag: boolean) => undefined,
  instanceId: '',
  setInstanceId: (id: string) => undefined,
  promptCancellationStatus: STATUS.DEFAULT,
  setPromptCancellationStatus: (status: STATUS) => undefined,
  closeRef: null,
  showCancelButton: null,
  setShowCancelButton: (flag: any) => undefined,
};

export const ChatbotContext = createContext<ChatbotContextType>(defaultChatbotContext);

export const ChatbotProvider = (props: any) => {
  // Props variable to reset the app(from App component)
  const { resetApp, setResetApp } = props;

  // State variable to keep track of the active chat tab (Response Details or Storybuilder)
  const [activeChatTab, setActiveChatTab] = useState<CHAT_TAB_VIEWS>(CHAT_TAB_VIEWS.RESPONSE_DETAILS);
  // State variable to keep track of the selected response ID
  const [selectedResponseId, setSelectedResponseId] = useState<string | null>(null);
  // List of sources to be shown in the Response Details tab
  const [responseDetailsSources, setResponseDetailsSources] = useState<DetailSource>({
    showCitation: false,
    sourceList: [],
  });
  // History of the sources for each message in the chat history
  const [sourceHistory, setSourceHistory] = useState<{ [key: string]: DetailSource }>({});
  // History of the search batches for each message in the chat history
  const [searchBatchesHistory, setSearchBatchesHistory] = useState<{ [key: string]: any }>({});
  // List of responses to be shown in the Storybuilder tab
  const [storyResponses, setStoryResponses] = useState<ResponseWithSource[]>([]);
  // Concatenated string to keeps track of the chat history to pass to the LLM as context
  const [chatHistory, setChatHistory] = useState(`AI: ${INITIAL_CHATBOT_MESSAGE}\n`);
  // Boolean value from chat response to display citation based on flag value
  const [showCitation, setShowCitation] = useState(true);
  //state holds the MAP like datastructure {'responseId' : 'genArcPromptAnalyticsUid'}
  const [genArcPromptAnalyticsUId, setGenArcPromptAnalyticsUId] = useState<{ [key: string]: any }>({});
  //state holds the MAP like datastructure {'responseId' : 'promptName'}
  const [genArcHistoryPrompts, setGenArcHistoryPrompts] = useState<{ [key: string]: any }>({});
  //State variable used to store chat history information
  const [chatPromptResponseHistory, setChatPromptResponseHistory] = useState<ChatHistory[]>([]);
  const [selectedProject, setSelectedProject] = useState<string[] | null>(null);
  const [selectedTranscript, setSelectedTranscript] = useState<string | null>('Primary');
  const [isLikeActive, setIsLikeActive] = useState<boolean>(false);
  const [isDislikeActive, setIsDislikeActive] = useState<boolean>(false);
  //state holds the conversation which exist in chat window.
  const [chatWindowConversations, setChatWindowConversations] = useState([]);
  const [feedbackSubmit, setFeedbackSubmit] = useState<boolean>(false);
  //flag to open Chat History Panel
  const [showChatHistoryPanel, setShowChatHistoryPanel] = useState<boolean>(false);
  //flag to open Right Panel
  const [showRightPanel, setShowRightPanel] = useState<boolean>(true);
  //state holds information about chat history window activated.
  const [isChatHistoryActive, setIsChatHistoryActive] = useState<boolean>(false);
  //state holds the prompt entered in chat-input field
  const [prompt, setPrompt] = useState<string>('');
  //flag to fetch chat history
  const [fetchChatHistoryFlag, setFetchChatHistoryFlag] = useState<boolean>(true);
  //state to store active chat uid
  const [activeChatUID, setActiveChatUID] = useState<string>('');
  //state holds genArcPromptAnalydticsUId of prompt which being generated
  const [currentGenArcPromptAnalyticsUId, setCurrentGenArcPromptAnalyticsUId] = useState<string>('');
  //state holds the boolean value to check if prompt is being cancelled
  const [isPromptCancelling, setIsPromptCancelling] = useState(false);
  //state holds the instanceId, which used in prompt cancel request object
  //will be Received from stepResponse
  const [instanceId, setInstanceId] = useState('');
  //state to store confidence score
  const [highScore, setHighScore] = useState(0);
  //state holds the status of prompt which is being cancelled
  const [promptCancellationStatus, setPromptCancellationStatus] = useState<STATUS>(STATUS.DEFAULT);
  const closeRef = useRef(null);
  const [showCancelButton, setShowCancelButton] = useState<any>();

  return (
    <ChatbotContext.Provider
      value={{
        resetApp,
        setResetApp,
        selectedResponseId,
        setSelectedResponseId,
        responseDetailsSources,
        setResponseDetailsSources,
        sourceHistory,
        setSourceHistory,
        searchBatchesHistory,
        setSearchBatchesHistory,
        storyResponses,
        setStoryResponses,
        chatHistory,
        setChatHistory,
        activeChatTab,
        setActiveChatTab,
        showCitation,
        setShowCitation,
        genArcPromptAnalyticsUId,
        setGenArcPromptAnalyticsUId,
        genArcHistoryPrompts,
        setGenArcHistoryPrompts,
        chatPromptResponseHistory,
        setChatPromptResponseHistory,
        selectedProject,
        setSelectedProject,
        selectedTranscript,
        setSelectedTranscript,
        isLikeActive,
        setIsLikeActive,
        isDislikeActive,
        setIsDislikeActive,
        chatWindowConversations,
        setChatWindowConversations,
        feedbackSubmit,
        setFeedbackSubmit,
        showChatHistoryPanel,
        setShowChatHistoryPanel,
        showRightPanel,
        setShowRightPanel,
        isChatHistoryActive,
        setIsChatHistoryActive,
        prompt,
        setPrompt,
        fetchChatHistoryFlag,
        setFetchChatHistoryFlag,
        activeChatUID,
        setActiveChatUID,
        currentGenArcPromptAnalyticsUId,
        setCurrentGenArcPromptAnalyticsUId,
        isPromptCancelling,
        setIsPromptCancelling,
        instanceId,
        setInstanceId,
        highScore,
        setHighScore,
        promptCancellationStatus,
        setPromptCancellationStatus,
        closeRef,
        showCancelButton,
        setShowCancelButton,
      }}
    >
      {props.children}
    </ChatbotContext.Provider>
  );
};
